import _ from "lodash";
import React, { useMemo } from "react";
import { isGlobalHeaderOnMultipleScreensEnabled } from "Utils";

// Components
import I18nHOC from "UIComponents/I18nHOC";
import withLoader from "UIComponents/GqlWithLoader";
import GroupedListItem from "./GroupedListItem";
import { FILTER_ITEMS } from "./GroupedListItem/GroupedListItem";

// Design system
import { Checkbox, SearchInput, EmptyState } from "@toddle-edu/ds-web";
import {
  AcademicHonestyIllustration,
  NoSearchResultsIllustration,
} from "@toddle-edu/ds-theme";

// External libs.
import { compose } from "lib/apolloClient";

// Scss
import classes from "./GroupedList.scss";
import { Trans } from "react-i18next";
import PaginationLoader from "UIComponents/PaginationLoader/PaginationLoader";

const EmptyStateComp = props => {
  const {
    searchValue,
    showSearchInput,
    emptySearchIllustration,
    emptySearchTitle,
    emptySearchSubtitle,
    emptyItemsIllustration,
    emptyItemsTitle,
    emptyItemsSubtitle,
  } = props;

  const showSearchEmptyState = showSearchInput && !_.isEmpty(searchValue);

  const illustration = showSearchEmptyState
    ? emptySearchIllustration
    : emptyItemsIllustration;

  const title = showSearchEmptyState ? emptySearchTitle : emptyItemsTitle;

  const subtitle = showSearchEmptyState
    ? emptySearchSubtitle
    : emptyItemsSubtitle;

  return (
    <EmptyState illustration={illustration} title={title} subtitle={subtitle} />
  );
};

const GroupedListContent = React.memo(props => {
  const {
    t,
    items,
    selectedItems,
    setSelectedItems,
    showEmptyState,
    subject,
    category,
    leftContainerStyle,
    optionContainerStyle,
    groupOptionContainerStyle,
    itemContainerClass,
    hasNextPage,
    loaderRef,
    testId,
    showGroupedList,
  } = props;

  const getIsChecked = itemId => {
    return _.findIndex(selectedItems, ({ id }) => _.isEqual(id, itemId)) !== -1;
  };

  const groupsStatus = useMemo(() => {
    const groupsResult = {};
    _.forEach(items, item => {
      const { id, options } = item;
      const selectedItemsIds = _.map(selectedItems, ({ id }) => id);
      groupsResult[id] = _.every(options, ({ id }) =>
        _.includes(selectedItemsIds, id)
      );
    });
    return groupsResult;
  }, [items, selectedItems]);

  const handleOnGroupClick = item => {
    const { id, options: selectedGroupOptions } = item;
    const selectedGroupStatus = _.get(groupsStatus, `${id}`);

    if (selectedGroupStatus) {
      const selectedGroupOptionsIds = _.map(
        selectedGroupOptions,
        ({ id }) => id
      );
      const filteredItems = _.filter(
        selectedItems,
        ({ id }) => !_.includes(selectedGroupOptionsIds, id)
      );
      setSelectedItems(
        _.map(filteredItems, item => _.omit(item, FILTER_ITEMS))
      );
    } else {
      const combinedItems = _.concat(selectedItems, selectedGroupOptions);
      const uniqueSelectedItems = _.uniqBy(combinedItems, "id");
      setSelectedItems(
        _.map(uniqueSelectedItems, item => _.omit(item, FILTER_ITEMS))
      );
    }
  };

  return _.isEmpty(items) && showEmptyState ? (
    <EmptyStateComp {...props} />
  ) : (
    <>
      {category && subject && (
        <div className={classes.scoreCategoryLabelText}>
          <Trans i18nKey="classRoom:showing_grades_with_category">
            Showing grades with the category
            <span className={classes.categoryText}>{{ category }}</span>
            for
            <span className={classes.categoryText}>{{ subject }}</span>
          </Trans>
        </div>
      )}
      {_.map(items, item => {
        const { id, label = "", icon = null, options = [] } = item || {};

        return (
          <React.Fragment key={id}>
            {showGroupedList ? (
              <div
                className={classes.groupOptionContainer}
                style={groupOptionContainerStyle}
                data-test-id={`${testId}-groupedList-class-${id}`}
                onClick={() => handleOnGroupClick(item)}
              >
                <div className={classes.iconAndLabelContainer}>
                  {icon ? <div className={classes.noShrink}>{icon}</div> : null}
                  <div className={classes.labelContainer}>{label}</div>
                </div>
                <Checkbox isChecked={groupsStatus[id]} />
              </div>
            ) : null}

            {_.map(options, option => (
              <GroupedListItem
                t={t}
                testId={`${testId}-groupedList-class-${id}`}
                key={option?.id}
                option={option}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                isChecked={getIsChecked(option?.id)}
                leftContainerStyle={leftContainerStyle}
                optionContainerStyle={optionContainerStyle}
                itemContainerClass={itemContainerClass}
              />
            ))}
          </React.Fragment>
        );
      })}
      {hasNextPage && (
        <div ref={loaderRef}>
          <PaginationLoader />
        </div>
      )}
    </>
  );
});

const GroupedListWithLoader = withLoader(GroupedListContent);

const GroupedList = React.memo(props => {
  const {
    t,
    placeholder,
    searchValue,
    onChangeSearchValue,
    showSearchInput,
    containerStyle,
    testId,
    additionalFilter,
  } = props;
  const isGlobalHeaderEnabled = isGlobalHeaderOnMultipleScreensEnabled();

  return (
    <div className={classes.container} style={containerStyle}>
      <div
        className={
          additionalFilter
            ? classes.searchInputContainerWithFilter
            : classes.searchInputContainer
        }
      >
        {showSearchInput && (
          <SearchInput
            testId={`${testId}-groupedList`}
            placeholder={placeholder ?? t("common:search")}
            value={searchValue}
            onChange={onChangeSearchValue}
            dsVersion={isGlobalHeaderEnabled ? "2.0" : "1.0"}
            focusSearchBySlash={true}
          />
        )}
        {additionalFilter}
      </div>
      <GroupedListWithLoader t={t} {...props} />
    </div>
  );
});

GroupedList.displayName = "GroupedList";
GroupedListContent.displayName = "GroupedListContent";

GroupedList.defaultProps = {
  showSearchInput: false,
  isData: true,
  isLoading: false,
  showEmptyState: false,
  emptySearchIllustration: NoSearchResultsIllustration,
  emptySearchTitle: "",
  emptySearchSubtitle: "",
  emptyItemsIllustration: AcademicHonestyIllustration,
  emptyItemsTitle: "",
  emptyItemsSubtitle: "",
  containerStyle: {},
  leftContainerStyle: {},
  optionContainerStyle: {},
  groupOptionContainerStyle: {},
  testId: "UIComponents",
  showGroupedList: true,
};

export default compose(I18nHOC({ resource: ["common"] }))(GroupedList);
